import { useCallback, useContext, useState } from "react";
import { Button, Fieldset, FilePicker, StatusDot } from "@rv/aura-web";
import "../styles/DefaultComponent.css";
import "../styles/Activate.css";
import { StateContext } from "../context/StateContext";
import { RequestParameters } from "../types/RequestParameters";
import { NavigationRight, AccessKey } from "@rv/aura-icons";
import {
  ActivateButtonWrapper,
  InvalidFile,
  PanelContent,
  UploadButtonWrapper,
} from "./ContentsStyled";
import labels from "../translation/index.json";

export default function UploadFile() {
  const [hasFileBeenUploaded, setHasFileBeenUploaded] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [selectedFilename, setSelectedFilename] = useState<
    string | undefined
  >();

  const appState = useContext(StateContext);
  const { requestParameters } = appState.currentState;

  const onActivateClicked = () => {
    if (!requestParameters) {
      return;
    }
    appState.updateState({ status: "request-in-progress", requestParameters });
  };

  const onFileChosen = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setFileError(false);
    const selectedFile: File = files.item(0) as File;
    selectedFile.text().then((data) => {
      setSelectedFilename(selectedFile.name);
      try {
        const fileContents = JSON.parse(data);
        if (
          fileContents["productName"] &&
          fileContents["generatedBy"] &&
          fileContents["requestType"] &&
          fileContents["creationDate"] &&
          fileContents["productKey"] &&
          fileContents["encryptedPayload"]
        ) {
          appState.updateState({
            status: "provide-file",
            requestParameters: fileContents,
          });
        } else {
          setFileError(true);
          appState.updateState({
            requestParameters: {
              requestType: "deactivation",
            } as RequestParameters,
            status: "provide-file",
          });
        }
      } catch {
        // Invalid file
        setFileError(true);
        appState.updateState({
          requestParameters: {
            requestType: "deactivation",
          } as RequestParameters,
          status: "provide-file",
        });
      }
    });
  };

  const filePicker = (title: string, withTextField: boolean = false) => (
    <FilePicker
      accept={[".json"]}
      allowMultipleSelection={false}
      onFileChosen={onFileChosen}
      placeholder={selectedFilename}
      browseLabel={title}
      className={withTextField ? "file-picker-input" : "file-picker-button"}
      withTextField={withTextField}
      browseButtonVariant="iconLeft"
      browseIcon={withTextField ? undefined : NavigationRight}
    />
  );

  const handleProcessClick = (type?: string) => {
    setHasFileBeenUploaded(true);
    appState.updateState({
      requestParameters: {
        requestType: type === "a" ? "activation" : "deactivation",
      } as RequestParameters,
      status: "provide-file",
    });
  };

  if (!hasFileBeenUploaded) {
    return (
      <div>
        <PanelContent>
          <p>{labels.welcomeMessage}</p>
          <p>{labels.welcomeQuestion}</p>
        </PanelContent>

        <UploadButtonWrapper>
          <Button
            label={labels.activateLicense}
            Icon={NavigationRight}
            variant="iconLeft"
            onClick={() => handleProcessClick("a")}
          />
          <Button
            label={labels.deactivateLicense}
            Icon={NavigationRight}
            variant="iconLeft"
            onClick={() => handleProcessClick()}
          />
        </UploadButtonWrapper>
      </div>
    );
  }

  const startOver = () => {
    setHasFileBeenUploaded(false);
    appState.updateState({
      requestParameters: undefined,
      status: "provide-file",
    });
  };

  return (
    <>
      <PanelContent
        align="start"
        justify="start"
        height="228px"
        paddingBottom={requestParameters ? 0 : 16}
      >
        <p>
          {labels.activationRequestGuide
            .replace(
              "$1",
              requestParameters?.requestType === "activation"
                ? "Activation (Checkout)"
                : "Deactivation (Checkin)",
            )
            .replace("$2", requestParameters?.requestType || "")}
        </p>
        {filePicker(labels.browse, true)}

        {requestParameters?.generatedBy || fileError ? (
          <Fieldset className="fieldSet" caption={labels.licenseRequestDetails}>
            {requestParameters && requestParameters.generatedBy ? (
              <>
                <div>Requested Date: {requestParameters.creationDate}</div>
                <div>Requested By: {requestParameters.generatedBy}</div>
                <div>Product Key: {requestParameters.productKey}</div>
              </>
            ) : null}

            {fileError ? (
              <InvalidFile>
                <StatusDot type="error" className="invalid-file" />
                {labels.invalidFile}
              </InvalidFile>
            ) : null}
          </Fieldset>
        ) : null}
      </PanelContent>
      <ActivateButtonWrapper>
        {requestParameters ? (
          <Button
            className="activate-button"
            label={
              requestParameters.requestType === "activation"
                ? labels.activateLicense
                : labels.deactivateLicense
            }
            Icon={AccessKey}
            variant="iconLeft"
            onClick={onActivateClicked}
            disabled={!requestParameters.generatedBy}
          />
        ) : (
          <Button
            className=""
            label={labels.activateOrDeactivate}
            Icon={NavigationRight}
            variant="iconLeft"
            onClick={startOver}
          />
        )}
      </ActivateButtonWrapper>
    </>
  );
}
